import type {
  ChangeEventHandler,
  Dispatch,
  FocusEventHandler,
  MouseEventHandler,
  MutableRefObject,
  PropsWithChildren,
  Ref,
} from 'react'
import React, {
  Children,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Divider from '../Divider/Divider'
import { useSyncRefs } from '../hooks/useSyncRefs'
import { useWindowSize } from '../hooks/useWindowSize'
import Menu, { useMenu } from '../Menu/Menu'
import { useMultiSelect } from '../MultiSelect/MultiSelect'
import Progress from '../Progress/Progress'
import type SvgIcon from '../SvgIcon/SvgIcon'
import type {
  TextInputColor,
  TextInputSize,
  TextInputVariant,
} from '../TextInput/styles'
import Typography from '../Typography/Typography'
import type { Actions } from './Actions'
import { ActionTypes } from './Actions'
import Group from './Group'
import Text from './Text'

export enum DisplayModes {
  Always = 'always',
  WithTerm = 'with-term',
  WithoutTerm = 'without-term',
}

export interface AutocompleteProps {
  creatable?: boolean
  clearable?: boolean
  color?: TextInputColor
  dataSet?: Record<string, string | undefined>
  defaultDisplayValue?: string
  defaultValue?: string
  displayRef?: Ref<HTMLInputElement>
  disabled?: boolean
  endAdornment?: typeof SvgIcon | string
  error?: boolean
  helperText?: string
  inputRef?: Ref<HTMLInputElement>
  label: string
  loading?: boolean
  name?: string
  onBlur?: FocusEventHandler<HTMLInputElement>
  onChange: ChangeEventHandler<HTMLInputElement>
  onFocus?: FocusEventHandler<HTMLInputElement>
  onSelect?: MouseEventHandler<HTMLButtonElement>
  select?: boolean
  size?: TextInputSize
  startAdornment?: typeof SvgIcon | string
  variant?: TextInputVariant
}

type AutocompleteState = {
  displayInputRef: MutableRefObject<HTMLInputElement>
  groups: Record<string, { display: DisplayModes; hasChildren: boolean }>
  inputRef: MutableRefObject<HTMLInputElement>
  label: string
  minWidth: string
  multi: boolean
  onSelectRef: MutableRefObject<
    MouseEventHandler<HTMLButtonElement> | undefined
  >
  searching: boolean
  select: boolean
  selectingRef: MutableRefObject<boolean>
  value: string
}

type AutocompleteReducers = {
  [ActionType in ActionTypes]: (
    state: AutocompleteState,
    action: Actions,
  ) => AutocompleteState
}

type UseAutocompleteValues = [AutocompleteState, Dispatch<Actions>]

const AutocompleteContext = createContext<UseAutocompleteValues | null>(null)

const reducers: AutocompleteReducers = {
  [ActionTypes.RegisterGroup]: (
    state,
    action: Extract<Actions, { type: ActionTypes.RegisterGroup }>,
  ) => ({
    ...state,
    groups: {
      ...state.groups,
      [action.id]: action.data,
    },
  }),
  [ActionTypes.UnregisterGroup]: (
    state,
    action: Extract<Actions, { type: ActionTypes.UnregisterGroup }>,
  ) => {
    const groups = { ...state.groups }
    delete groups[action.id]

    return { ...state, groups }
  },
  [ActionTypes.SetSearching]: (state) => ({ ...state, searching: true }),
  [ActionTypes.ClearSearching]: (state) => ({ ...state, searching: false }),
  [ActionTypes.Clear]: (state) => ({
    ...state,
    selecting: false,
    label: '',
    value: '',
  }),
  [ActionTypes.ClearSelecting]: (state) => ({ ...state, selecting: false }),
  [ActionTypes.SelectOption]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SelectOption }>,
  ) => {
    return {
      ...state,
      searching: state.multi ? state.searching : false,
      label: action.label || state.label,
      value: action.value,
    }
  },
  [ActionTypes.SetMinWidth]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SetMinWidth }>,
  ) => ({
    ...state,
    minWidth: action.value,
  }),
}

const runReducer = (
  state: AutocompleteState,
  action: Actions,
): AutocompleteState => reducers[action.type](state, action)

export const useAutocomplete = (): UseAutocompleteValues =>
  useContext(AutocompleteContext) as UseAutocompleteValues

function Autocomplete({
  creatable = false,
  clearable = false,
  children,
  color = 'default',
  dataSet,
  defaultDisplayValue = '',
  defaultValue = '',
  disabled = false,
  displayRef,
  endAdornment,
  error = false,
  helperText,
  inputRef = null,
  label,
  loading = false,
  name,
  onBlur,
  onChange,
  onFocus,
  onSelect,
  select = false,
  size = 'medium',
  startAdornment,
  variant = 'outlined',
}: PropsWithChildren<AutocompleteProps>): JSX.Element {
  const [multiState, multiDispatch] = useMultiSelect() ?? []
  const multi = !!multiDispatch

  const displayValue = defaultDisplayValue || defaultValue
  const localDisplayRef = useRef<HTMLInputElement>(null)
  const localInputRef = useRef<HTMLInputElement>(null)
  const selectingRef = useRef(false)
  const onSelectRef = useRef(onSelect)
  const intl = useIntl()

  const [state, dispatch] = useReducer(runReducer, null, () => {
    return {
      displayInputRef: localDisplayRef,
      groups: {},
      inputRef: localInputRef,
      label: displayValue,
      multi,
      onSelectRef,
      select,
      // Indicates that the input label is updated by selecting a suggestion.
      // Therefore, we don't want to trigger any functionality associated with user typing
      selectingRef,
      searching: multi ? defaultDisplayValue : select ? false : !!defaultValue,
      value: defaultValue,
    } as AutocompleteState
  })
  const windowSize = useWindowSize(localDisplayRef)

  useEffect(() => {
    onSelectRef.current = onSelect
  }, [onSelect])

  const displaySyncRef = useSyncRefs(displayRef, localDisplayRef)
  const inputSyncRef = useSyncRefs(inputRef, localInputRef)

  const hasVisibleChildren = useMemo(() => {
    return (
      Children.count(children) &&
      (Object.keys(state.groups).length === 0 ||
        Object.values(state.groups).some(
          (group) =>
            group.display !== DisplayModes.WithoutTerm && group.hasChildren,
        ))
    )
  }, [children, state.groups])

  const [, menuDispatch] = useMenu() ?? []
  const inMenu = !!menuDispatch

  useLayoutEffect(() => {
    localDisplayRef.current &&
      dispatch({
        type: ActionTypes.SetMinWidth,
        value: window
          .getComputedStyle(localDisplayRef.current)
          .width.toString(),
      })
  }, [localDisplayRef, windowSize.width, menuDispatch])

  const input = (
    <Text
      clearable={clearable && select}
      color={color}
      dataSet={dataSet}
      defaultDisplayValue={typeof displayValue === 'string' ? displayValue : ''}
      defaultValue={defaultValue}
      disabled={disabled}
      displayRef={displaySyncRef}
      endAdornment={endAdornment}
      error={error}
      helperText={helperText}
      inMenu={inMenu}
      inputRef={inputSyncRef}
      label={label}
      name={name}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      size={size}
      startAdornment={startAdornment}
      variant={variant}
    />
  )

  const items = (
    <>
      {loading ? (
        <div className="flex justify-center px-3 py-1">
          <Progress variant="circular" />
        </div>
      ) : (
        children
      )}
      {!loading &&
      !creatable &&
      (state.multi || state.searching) &&
      !hasVisibleChildren ? (
        <div className="px-4 py-2 text-left cursor-default select-none text-black-60">
          <Typography color="inherit" variant="subtitle2">
            <FormattedMessage id="Ui.no_results" />
          </Typography>
        </div>
      ) : null}
      {!loading && creatable && state.searching && !hasVisibleChildren ? (
        <Menu.Item>
          <span>
            {intl.formatMessage(
              {
                id: 'Autocomplete.add',
              },
              {
                value: localDisplayRef.current?.value,
              },
            )}
          </span>
        </Menu.Item>
      ) : null}
    </>
  )

  const onClose = () => {
    multiState?.handleClose?.()
  }

  return (
    <AutocompleteContext.Provider value={[state, dispatch]}>
      {inMenu && multi ? (
        <>
          <Menu.Item applyFocusRef={localDisplayRef} selectable={false}>
            <div className="px-1 pt-1">{input}</div>
          </Menu.Item>
          {!loading && multiState?.actions && hasVisibleChildren ? (
            <>
              <li className="flex px-4 pb-3 space-x-2">
                {multiState?.actions}
              </li>
              <Divider />
            </>
          ) : null}
          {items}
        </>
      ) : (
        <div>
          <Menu onClose={onClose}>
            {input}
            <Menu.Items>
              {multi &&
              !loading &&
              multiState?.actions &&
              hasVisibleChildren ? (
                <>
                  <li className="flex px-3 mb-2 -mt-2">
                    <ul>{multiState?.actions}</ul>
                  </li>
                  <Divider />
                </>
              ) : null}
              {items}
            </Menu.Items>
          </Menu>
        </div>
      )}
    </AutocompleteContext.Provider>
  )
}

interface SuggestionProps {
  disabled?: boolean
  value: string
  displayValue?: string
}

interface SuggestionExtraProps {
  checkbox: boolean
  defaultChecked: boolean
  onChange: ChangeEventHandler
}

function Suggestion({
  children,
  disabled = false,
  displayValue,
  value,
  ...rest
}: PropsWithChildren<SuggestionProps>): JSX.Element {
  const extraProps = rest as SuggestionExtraProps

  const [state, dispatch] = useAutocomplete()
  const ref = useRef<HTMLButtonElement | null>(null)
  const selected = state.value === value // always false for multi

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!disabled) {
      event.persist()

      const input = state.inputRef?.current
      if (input) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(input, state.select ? value : '')
        input.dispatchEvent(inputEvent)
      }

      const display = state.displayInputRef?.current
      if (display) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(
          display,
          state.select && value
            ? displayValue || event.currentTarget.textContent || ''
            : '',
        )
        display.dispatchEvent(inputEvent)
      }

      if (state.select) {
        state.selectingRef.current = true

        dispatch({
          type: ActionTypes.SelectOption,
          label: value
            ? displayValue || event.currentTarget.textContent || ''
            : '',
          value,
        })
      }

      state.onSelectRef.current?.(event)
    }
  }

  const handler = state.multi
    ? { onChange: extraProps?.onChange }
    : { onClick: handleClick }

  return (
    <Menu.Item
      {...(extraProps?.checkbox
        ? { checkbox: true, defaultChecked: extraProps.defaultChecked }
        : {})}
      disabled={disabled}
      ref={ref}
      selected={selected}
      value={value}
      {...handler}
    >
      {children}
    </Menu.Item>
  )
}

Autocomplete.Group = Group
Autocomplete.Suggestion = Suggestion

export default Autocomplete
